/**
 * AlertDialog — a reusable, single-button modal for surfacing a message that the user must
 * acknowledge (validation/save errors, notices). Unlike a toast or an inline banner, it is
 * centered and blocking, so it's seen regardless of scroll position. For a two-button
 * confirm/cancel flow use `ConfirmDialog` instead.
 */
import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GgcButton } from '@/components/ui/ggc/button';
import { useI18nText } from '@/lib/utils/i18nText';

export type AlertDialogTone = 'error' | 'warning' | 'info' | 'success';

const TONE_ICON: Record<AlertDialogTone, string> = {
  error: 'fa-exclamation-circle text-ggc-red',
  warning: 'fa-exclamation-triangle text-ggc-orange',
  info: 'fa-info-circle text-ggc-cta-blue',
  success: 'fa-check-circle text-ggc-green',
};

export interface AlertDialogProps {
  /** Whether the dialog is open. Omit to derive it from `message` (open when message is set). */
  open?: boolean;
  /** Body text. Newlines are preserved. */
  message?: ReactNode;
  /** Heading; defaults to a localized "Notice". */
  title?: ReactNode;
  /** Icon + accent for the heading. Default `error`. */
  tone?: AlertDialogTone;
  /** Confirm-button label; defaults to a localized "OK". */
  confirmLabel?: ReactNode;
  /** Called when the dialog is dismissed (OK / overlay / Esc). */
  onClose: () => void;
}

export function AlertDialog({ open, message, title, tone = 'error', confirmLabel, onClose }: AlertDialogProps) {
  const { t } = useI18nText();
  const isOpen = open ?? (message != null && message !== '');
  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            <i className={`fa ${TONE_ICON[tone]} mr-2`} aria-hidden />
            {title ?? t('Notice', '안내')}
          </DialogTitle>
          {/* Radix requires an accessible description (aria-describedby). Kept hidden and
              separate from the visible message below so it never affects that body's styling. */}
          <DialogDescription className="sr-only">{title ?? t('Notice', '안내')}</DialogDescription>
        </DialogHeader>
        {message != null && (
          <p className="ggc-text-body text-ggc-text-primary whitespace-pre-line">{message}</p>
        )}
        <DialogFooter>
          <GgcButton type="button" variant="primary" onClick={onClose}>
            {confirmLabel ?? t('OK', '확인')}
          </GgcButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
